/**
 * A re-grade compared with its source run, also while it runs. The human flags on the
 * source are the labels: a flagged check whose verdict changed is counted as fixed.
 * Changes on checks nobody flagged can be regressions, so they are listed for a human to read.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import { evalsService } from '../../services/evalsService';
import { isActiveRun, type EvalRunItem, type RegradeCheckChange, type RegradeReport } from '../../types/evalTypes';
import { formatDateTime, formatPercent } from '../../utils/evalFormat';

interface RegradeReportPanelProps {
  run: EvalRunItem;
  onShow: (caseId: string, armId: string) => void;
}

const verdictClass = (verdict: RegradeCheckChange['after']): string => {
  if (verdict === 'pass') return 'text-green-700';
  if (verdict === 'fail') return 'text-red-700';
  return 'text-amber-700';
};

const ChangeTable = ({ changes, onShow, showNote }: { changes: RegradeCheckChange[]; onShow: RegradeReportPanelProps['onShow']; showNote: boolean }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs text-gray-500">
        <tr>
          <th className="font-medium px-3 py-2">Result</th>
          <th className="font-medium px-3 py-2">Check</th>
          <th className="font-medium px-3 py-2">Before → after</th>
          <th className="font-medium px-3 py-2">{showNote ? 'Human note / new reason' : 'New reason'}</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((change) => (
          <tr key={`${change.resultId}~${change.checkId}`} className="border-t border-gray-100 align-top">
            <td className="px-3 py-2 whitespace-nowrap">
              <button type="button" onClick={() => onShow(change.caseId, change.armId)} className="text-blue-600 hover:underline font-mono text-xs">
                {change.caseId}
              </button>
              <span className="block text-xs text-gray-500">
                {change.armId} · trial {change.trial}
              </span>
            </td>
            <td className="px-3 py-2 font-mono text-xs">{change.checkId}</td>
            <td className="px-3 py-2 whitespace-nowrap">
              <span className={verdictClass(change.before)}>{change.before}</span> →{' '}
              <span className={verdictClass(change.after)}>{change.after ?? 'none'}</span>
            </td>
            <td className="px-3 py-2 text-xs text-gray-700 whitespace-pre-wrap">
              {showNote && change.flagNote && <span className="block text-amber-900 mb-1">Human: {change.flagNote}</span>}
              {change.detail}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** "v3 → v4" for each judge, from the source run's versions to the re-grade's. */
const judgeChange = (report: RegradeReport, run: EvalRunItem): string =>
  run.judgePrompts
    .map((after) => {
      const before = report.sourceJudgePrompts.find((ref) => ref.judgeName === after.judgeName);
      return `${after.judgeName} v${before?.number ?? '?'} → v${after.number}`;
    })
    .join(', ');

export const RegradeReportPanel = ({ run, onShow }: RegradeReportPanelProps) => {
  const [report, setReport] = useState<RegradeReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const active = isActiveRun(run.status);
  const isRegrade = run.regradeOf !== undefined;

  // Loaded again whenever the page's refresh brings new results, so it grows as the
  // re-grade runs.
  useEffect(() => {
    if (!isRegrade) return;
    let cancelled = false;
    evalsService.getRegradeReport(run.runId).then(
      (loaded) => !cancelled && setReport(loaded),
      (caught: unknown) => !cancelled && setError(caught instanceof Error ? caught.message : 'Failed to load the re-grade report.'),
    );
    return () => {
      cancelled = true;
    };
  }, [run.runId, isRegrade, run.status, run.resultCount]);

  if (!run.regradeOf) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Re-grade report</h2>
        <p className="text-sm text-gray-600">
          Re-grade of{' '}
          <Link to={`/evals/${run.regradeOf.runId}`} className="text-blue-600 hover:underline">
            {report ? `the run from ${formatDateTime(report.sourceStartedAt)}` : 'the source run'}
          </Link>
          {report && run.judgePrompts && ` · judge ${judgeChange(report, run)}`}
          {' · '}
          {run.regradeOf.scope === 'flagged' ? 'only the results with a judge flag' : 'every result with an output'}. The same
          outputs, graded again.
        </p>
      </div>

      {active && (
        <p className="text-sm text-blue-800 bg-blue-50 rounded-lg px-3 py-2">
          {run.resultCount} of {run.total} graded so far
          {report && report.flagged.total > 0 && ` · ${report.flagged.fixed} of ${report.flagged.total} flagged checks fixed so far`}. The
          numbers below grow as the judge works.
        </p>
      )}
      {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}

      {report && (
        <>
          <dl className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
            <div>
              <dt className="text-xs text-gray-500">Flagged checks</dt>
              <dd className="text-lg font-semibold">{report.flagged.total}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Fixed</dt>
              <dd className="text-lg font-semibold text-green-700">{report.flagged.fixed}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Same verdict</dt>
              <dd className="text-lg font-semibold text-red-700">{report.flagged.same}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Unknown</dt>
              <dd className="text-lg font-semibold text-amber-700">{report.flagged.unknown}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Passed, before → after</dt>
              <dd className="text-lg font-semibold">
                {formatPercent(report.passRate.before)} → {formatPercent(report.passRate.after)}
              </dd>
            </div>
          </dl>

          {report.flagged.checks.length > 0 && (
            <section className="space-y-1">
              <h3 className="text-sm font-semibold text-gray-800">Checks humans flagged</h3>
              <ChangeTable changes={report.flagged.checks} onShow={onShow} showNote />
            </section>
          )}

          <section className="space-y-1">
            <h3 className="text-sm font-semibold text-gray-800">Other checks that changed ({report.otherChanges.length})</h3>
            {report.otherChanges.length === 0 ? (
              <p className="text-sm text-gray-500">None: every check nobody flagged has the same verdict.</p>
            ) : (
              <>
                <p className="text-xs text-gray-500">Nobody flagged these, so each one can be an improvement or a regression. Read them.</p>
                <ChangeTable changes={report.otherChanges} onShow={onShow} showNote={false} />
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
};
